import { hasLength } from 'utility/functions';
import { InputChoice } from '../dropdownConsts';
import { SelectDropdownInputProps } from './SelectDropdownInput';

interface deduplicateInputChoicesParams {
  staticChoices: InputChoice[];
  possiblyUnknownItems: InputChoice[];
  fetchedData: InputChoice[];
  prioritizeStaticChoices?: boolean;
}

// future question: we can add .sort() chaining or pin client/unknown items to top of list
// to prevent initial client side items from jumping around on more infinity load...
// but idk if we should override the backend provided sorting order...
export function deduplicateInputChoices({
  staticChoices = [],
  fetchedData = [],
  possiblyUnknownItems = [],
  prioritizeStaticChoices = false,
}: deduplicateInputChoicesParams): InputChoice[] {
  // merge in the desired order

  const allChoices = prioritizeStaticChoices
    ? [...staticChoices, ...fetchedData, ...possiblyUnknownItems]
    : [...fetchedData, ...staticChoices, ...possiblyUnknownItems];

  const seen = new Map<string, InputChoice>();
  const deduped: InputChoice[] = [];

  for (const choice of allChoices) {
    // skip invalid entries, items need a label and value minimum
    if (!choice || !choice.hasOwnProperty('label') || !choice.hasOwnProperty('value')) {
      continue;
    }
    // only add the first time we see this value (convert to string for consistent comparison)
    const valueKey = String(choice.value);
    if (!seen.has(valueKey)) {
      seen.set(valueKey, choice);
      deduped.push(choice);
    }
  }

  return deduped;
}

// There can be a situation in which with inf scroll / query params, that we haven't loaded an item's label
// for instance, if its value was loaded from query params and the label wasn't in the initial fetch.
// CMSADMIN-285 Dev Notes - We'll need the API endpoints to support similar to the editorial/asset/search uuid[] field
// where we can, in the fetchDataFn pass a paramater like include[], to be forcefully incuded in the response, in addition to any other data.
// This is also required for link sharing and infinity scroll, as we wouldn't
// know how deep an item's value is in the list of infinite scroll data before the label may be resolved.

// determine which items are unlabeled
// eg: the value exists in the query params
// but no corresponding value exists
// in the static or fetched data (may not be loaded yet)
export function getUnlabeledItemsByValue(
  staticChoices: InputChoice[],
  fetchedData: InputChoice[],
  values: string[], // selected values
): InputChoice[] {
  // build a set of all values (converted to strings for comparison)
  const known = new Set<string>([
    ...staticChoices.map((c) => String(c?.value)),
    ...fetchedData.map((c) => String(c?.value)),
  ]);

  const seen = new Set<string>();
  const unlabeled: InputChoice[] = [];

  for (const v of values) {
    // skip if no value or if already known or seen
    if (!v || known.has(v) || seen.has(v)) {
      continue;
    }
    // if the value is not known, add it as an item
    // have to use the value as the label since we
    // havent fetched the label yet via api...
    seen.add(v);
    unlabeled.push({ label: v, value: v });
  }

  return unlabeled;
}

export function filterChoicesByText(
  choices: InputChoice[],
  filterText: string,
): InputChoice[] {
  if (!filterText) return choices;

  const lowercasedFilterText = filterText.toLowerCase();
  const filteredChoices: InputChoice[] = [];

  let currentHeader: InputChoice | null = null;
  let groupBuffer: InputChoice[] = [];

  for (const choice of choices) {
    if (choice.__isHeader) {
      // flush previous group if any
      if (groupBuffer.length > 0 && currentHeader) {
        filteredChoices.push(currentHeader, ...groupBuffer);
      }
      currentHeader = choice;
      groupBuffer = [];
      continue;
    }

    const labelMatch = choice.label?.toLowerCase().includes(lowercasedFilterText);
    const valueMatch = String(choice.value).toLowerCase().includes(lowercasedFilterText);
    const sublabelMatch = choice.sublabel?.toLowerCase().includes(lowercasedFilterText);
    const anyMatch = labelMatch || valueMatch || sublabelMatch;

    if (anyMatch) {
      if (currentHeader) {
        groupBuffer.push(choice);
      } else {
        filteredChoices.push(choice); // for ungrouped results
      }
    }
  }

  // Flush final group
  if (groupBuffer.length > 0 && currentHeader) {
    filteredChoices.push(currentHeader, ...groupBuffer);
  }

  return filteredChoices;
}

export function choiceTextString(choice: InputChoice): string {
  if (choice.selectedLabel) return choice.selectedLabel;
  return `${choice.label}${choice.sublabel ? ` ${choice.sublabel}` : ''}`;
}

export function getSelectPlaceholderString(
  inputPlaceholderText: SelectDropdownInputProps['inputPlaceholderText'],
  uniqueChoices: InputChoice[],
  inputLabel: string,
): string {
  switch (inputPlaceholderText) {
    case 'stringify-options': {
      if (!hasLength(uniqueChoices)) {
        return 'No results found...'; // No choices to display
      } else {
        // get only the first 10 choices and add ... if there are more (could be thousands)
        const previewString = uniqueChoices
          .slice(0, 10)
          .map((choice) => choiceTextString(choice))
          .join(', ');
        const result = uniqueChoices.length > 10 ? `${previewString}...` : previewString;
        return result;
      }
    }
    case null: {
      return ''; // Explicit null means blank box
    }
    default: {
      return inputPlaceholderText || inputLabel; // Fallback to input label if not provided
    }
  }
}

/**
 * Normalizes base values for select dropdowns based on the `multiple` prop.
 * - For multiple=true, returns an array of strings (never null/undefined).
 * - For multiple=false, returns a string (never null/undefined).
 *
 * @param baseValues - The value(s) to normalize (string, string[], null, or undefined)
 * @param multiple - Whether the select is multi-select
 * @returns string[] if multiple, string if not
 */
export function normalizeBaseValues(
  baseValues: string | string[] | null | undefined,
): string[] {
  if (Array.isArray(baseValues)) {
    return baseValues.filter((v) => typeof v === 'string').map(String);
  }
  return baseValues != null && baseValues !== '' ? [String(baseValues)] : [];
}
